import React, { useState } from 'react';
import api from '../../services/api';
import { useMessage } from '../../components/Message';

const GameModal = ({ game, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        gameId: game?.gameId || '',
        name: game?.name || '',
        category: game?.category || 'adventure',
        description: game?.description || '',
        instructions: game?.instructions || '',
        difficulty: game?.difficulty || 'medium',
        isActive: game?.isActive !== undefined ? game.isActive : true
    });
    const [loading, setLoading] = useState(false);
    const [checkingId, setCheckingId] = useState(false);
    const [idAvailable, setIdAvailable] = useState(true);
    const [idCheckMessage, setIdCheckMessage] = useState('');
    const { showMessage } = useMessage();

// In GameModal.js - update the checkGameIdAvailability function

const checkGameIdAvailability = async (id) => {
    // If no ID entered, skip check
    if (!id || id.trim() === '') {
        setIdAvailable(false);
        setIdCheckMessage('Please enter a game ID');
        return;
    }

    // If editing and ID hasn't changed, it's always available
    if (game && id === game.gameId) {
        setIdAvailable(true);
        setIdCheckMessage('✅ Current game ID (unchanged)');
        return;
    }

    setCheckingId(true);
    setIdCheckMessage('Checking availability...');
    
    try {
        // Add exclude parameter if editing
        let url = `/admin/games/check-id/${id}`;
        if (game) {
            url += `?exclude=${game.id}`; // Pass the UUID of the current game
        }
        
        const response = await api.get(url);
        const isAvailable = response.data.data.available;
        setIdAvailable(isAvailable);
        setIdCheckMessage(isAvailable ? '✅ ID is available' : '❌ ID is already taken');
    } catch (error) {
        // If the endpoint doesn't exist, fallback to trying to find the game directly
        try {
            const checkResponse = await api.get(`/admin/games/${id}`);
            setIdAvailable(false);
            setIdCheckMessage('❌ ID is already taken');
        } catch (err) {
            if (err.response?.status === 404) {
                setIdAvailable(true);
                setIdCheckMessage('✅ ID is available');
            } else {
                setIdAvailable(true);
                setIdCheckMessage('⚠️ Could not verify ID availability');
            }
        }
    } finally {
        setCheckingId(false);
    }
};

    const handleGameIdChange = (e) => {
        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
        setFormData({ ...formData, gameId: value });
        
        // Clear the availability status while typing
        if (value.length < 2) {
            setIdAvailable(false);
            setIdCheckMessage('ID must be at least 2 characters');
            return;
        }
        
        // Debounce the check
        clearTimeout(window.idCheckTimeout);
        window.idCheckTimeout = setTimeout(() => {
            checkGameIdAvailability(value);
        }, 500);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate gameId
        if (!formData.gameId || formData.gameId.trim() === '') {
            showMessage('Game ID is required', 'error');
            return;
        }

        // If editing and ID changed, verify it's available
        if (game && formData.gameId !== game.gameId && !idAvailable) {
            showMessage('This game ID is already taken. Please choose another.', 'error');
            return;
        }

        // If new game and ID not available
        if (!game && !idAvailable) {
            showMessage('This game ID is already taken. Please choose another.', 'error');
            return;
        }

        setLoading(true);

        try {
            if (game) {
                // Update existing game
                await api.put(`/admin/games/${game.gameId}`, {
                    gameId: formData.gameId,
                    name: formData.name,
                    category: formData.category,
                    description: formData.description,
                    instructions: formData.instructions,
                    difficulty: formData.difficulty,
                    isActive: formData.isActive
                });
                showMessage('Game updated successfully!', 'success');
            } else {
                // Create new game
                await api.post('/admin/games', {
                    gameId: formData.gameId,
                    name: formData.name,
                    category: formData.category,
                    description: formData.description,
                    instructions: formData.instructions,
                    difficulty: formData.difficulty,
                    isActive: formData.isActive,
                    gamePath: formData.gameId
                });
                showMessage('Game created successfully!', 'success');
            }
            onSave();
        } catch (error) {
            // Handle specific error cases
            if (error.response?.status === 400 && error.response?.data?.message?.includes('already exists')) {
                showMessage('This game ID is already taken. Please choose another.', 'error');
                setIdAvailable(false);
                setIdCheckMessage('❌ ID is already taken');
            } else {
                showMessage(error.response?.data?.message || 'Operation failed', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    // Determine if submit should be disabled
    const isSubmitDisabled = () => {
        if (loading) return true;
        if (!formData.gameId || formData.gameId.trim() === '') return true;
        if (!formData.name || formData.name.trim() === '') return true;
        // If editing with new ID, check availability
        if (game && formData.gameId !== game.gameId && !idAvailable) return true;
        // If new game, check availability
        if (!game && !idAvailable) return true;
        return false;
    };

    return (
        <div className="admin-modal-overlay" onClick={onClose}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                <h2 className="admin-modal-title">{game ? 'Edit Game' : 'Add New Game'}</h2>
                <form onSubmit={handleSubmit} className="admin-modal-form">
                    <div className="admin-form-group">
                        <label className="admin-form-label">Game ID (folder name):</label>
                        <input
                            type="text"
                            value={formData.gameId}
                            onChange={handleGameIdChange}
                            required
                            pattern="[a-z0-9-]+"
                            title="Use only lowercase letters, numbers, and hyphens"
                            disabled={loading}
                            placeholder="e.g., my-awesome-game"
                            className={`admin-form-input ${idCheckMessage.includes('available') ? 'admin-input-success' : idCheckMessage.includes('taken') || idCheckMessage.includes('required') ? 'admin-input-error' : ''}`}
                        />
                        {checkingId && (
                            <small className="admin-form-hint admin-hint-info">
                                🔄 Checking availability...
                            </small>
                        )}
                        {!checkingId && idCheckMessage && (
                            <small className={`admin-form-hint ${idCheckMessage.includes('available') ? 'admin-hint-success' : idCheckMessage.includes('taken') || idCheckMessage.includes('required') ? 'admin-hint-error' : 'admin-hint-info'}`}>
                                {idCheckMessage}
                            </small>
                        )}
                        <small className="admin-form-hint">
                            {game ? 
                                'Changing this will rename the game folder on the server' : 
                                'This will be the folder name and URL. Cannot be changed easily after creation.'
                            }
                        </small>
                    </div>

                    <div className="admin-form-group">
                        <label className="admin-form-label">Game Name:</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            disabled={loading}
                            placeholder="e.g., My Awesome Game"
                            className="admin-form-input"
                        />
                    </div>

                    <div className="admin-form-row">
                        <div className="admin-form-group">
                            <label className="admin-form-label">Category:</label>
                            <select
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                required
                                disabled={loading}
                                className="admin-form-select"
                            >
                                <option value="adventure">Adventure</option>
                                <option value="puzzle">Puzzle</option>
                                <option value="action">Action</option>
                                <option value="racing">Racing</option>
                            </select>
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-form-label">Difficulty:</label>
                            <select
                                value={formData.difficulty}
                                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                                required
                                disabled={loading}
                                className="admin-form-select"
                            >
                                <option value="easy">Easy 🌱</option>
                                <option value="medium">Medium ⭐</option>
                                <option value="hard">Hard 🔥</option>
                            </select>
                        </div>
                    </div>

                    <div className="admin-form-group">
                        <label className="admin-form-label">Description:</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows="4"
                            required
                            disabled={loading}
                            placeholder="Describe your game..."
                            className="admin-form-textarea"
                        />
                    </div>

                    <div className="admin-form-group">
                        <label className="admin-form-label">Instructions:</label>
                        <textarea
                            value={formData.instructions}
                            onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                            rows="3"
                            disabled={loading}
                            placeholder="How to play the game..."
                            className="admin-form-textarea"
                        />
                    </div>

                    <div className="admin-form-group admin-checkbox">
                        <label className="admin-form-label admin-checkbox-label">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                disabled={loading}
                                className="admin-form-checkbox"
                            />
                            Active (visible to users)
                        </label>
                    </div>

                    {!game && (
                        <div className="admin-info-box">
                            <p>📁 After creating the game, you can:</p>
                            <ul>
                                <li>Upload game files in the "Upload Game Files" tab</li>
                                <li>Add images in the "Manage Images" tab</li>
                                <li>The game will be available at: /games/{formData.gameId || 'game-id'}</li>
                            </ul>
                        </div>
                    )}

                    {game && formData.gameId !== game.gameId && (
                        <div className="admin-info-box admin-warning-box">
                            <p>⚠️ Warning: Changing Game ID</p>
                            <ul>
                                <li>The game ID will be updated in the database</li>
                                <li>The folder name will <strong>NOT</strong> be renamed (stays as "{game.gameId}")</li>
                                <li>All image URLs will use the new game ID</li>
                                <li>Existing links to the game may break</li>
                            </ul>
                        </div>
                    )}

                    <div className="admin-modal-actions">
                        <button 
                            type="submit" 
                            disabled={isSubmitDisabled()} 
                            className="admin-btn-save"
                        >
                            {loading ? 'Saving...' : (game ? 'Update Game' : 'Create Game')}
                        </button>
                        <button 
                            type="button" 
                            onClick={onClose} 
                            className="admin-btn-cancel"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GameModal;